import { useCallback, useRef, useState, type ReactNode } from 'react';

/**
 * Draggable bottom sheet.
 *
 * Hand-rolled because it sits over a live camera preview, must respect the
 * safe-area inset, and must never swallow the space the preview needs.
 *
 * It is written so it cannot get stuck. The earlier version tracked the drag in
 * a ref and computed height from it; when iOS cancelled the pointer stream
 * mid-drag the ref was never cleared and the sheet stayed pushed off the bottom
 * of the screen with no way back. Now:
 *  - height comes from the snap index, never from live drag state;
 *  - the drag only ever *chooses* a snap on release;
 *  - the smallest snap still shows the handle, so there is always something to
 *    pull or tap — and only the handle: collapsed, the sheet drops its card
 *    and leaves a bar floating over the camera, so no white strip covers the
 *    bottom of the preview;
 *  - pointercancel and lostpointercapture end the drag like a normal release.
 */
export type SheetSnap = 0 | 1 | 2;

/** Collapsed height: just the handle's tap target, above the home indicator. */
export const COLLAPSED_SHEET_CSS = 'calc(2rem + env(safe-area-inset-bottom))';

export interface BottomSheetProps {
  children: ReactNode;
  /** Fractions of the viewport for peek and expanded; the first (collapsed) is handle-only. */
  snaps?: [number, number, number];
  initial?: SheetSnap;
  /** Controlled height. When set, the parent owns the snap; drags still report through onSnapChange. */
  snap?: SheetSnap;
  /** Always-visible header, doubles as the drag handle. */
  header?: ReactNode;
  onSnapChange?(snap: SheetSnap): void;
}

const DRAG_THRESHOLD = 40;

export function BottomSheet({
  children,
  snaps = [0.09, 0.4, 0.85],
  initial = 1,
  snap: controlled,
  header,
  onSnapChange,
}: BottomSheetProps): ReactNode {
  const [inner, setSnap] = useState<SheetSnap>(controlled ?? initial);
  const snap = controlled ?? inner;
  const [dragging, setDragging] = useState(false);
  const startY = useRef<number | null>(null);

  const apply = useCallback(
    (next: SheetSnap) => {
      setSnap(next);
      onSnapChange?.(next);
    },
    [onSnapChange],
  );

  const step = useCallback(
    (direction: -1 | 1) => {
      const next = Math.min(2, Math.max(0, snap + direction)) as SheetSnap;
      if (next !== snap) apply(next);
    },
    [apply, snap],
  );

  const endDrag = useCallback(
    (clientY?: number) => {
      const from = startY.current;
      startY.current = null;
      setDragging(false);
      if (from === null || clientY === undefined) return;
      const delta = clientY - from;
      if (delta <= -DRAG_THRESHOLD) step(1);
      else if (delta >= DRAG_THRESHOLD) step(-1);
    },
    [step],
  );

  return (
    <div
      className={`absolute inset-x-0 bottom-0 z-20 flex flex-col overflow-hidden rounded-t-3xl transition-[background-color,box-shadow] ${
        snap === 0
          ? 'bg-transparent'
          : 'border-t border-line bg-card/95 shadow-[0_-8px_30px_rgba(15,23,42,0.18)] backdrop-blur'
      }`}
      style={{
        height: snap === 0 ? COLLAPSED_SHEET_CSS : `calc(${snaps[snap] * 100}dvh + env(safe-area-inset-bottom))`,
        transition: dragging ? 'none' : 'height 240ms ease',
      }}
    >
      <button
        type="button"
        // Tap cycles up, and wraps from the top back to collapsed, so the sheet
        // is always reachable with a single thumb and no drag at all.
        onClick={() => apply(snap === 2 ? 0 : ((snap + 1) as SheetSnap))}
        onPointerDown={(event) => {
          startY.current = event.clientY;
          setDragging(true);
        }}
        onPointerUp={(event) => endDrag(event.clientY)}
        onPointerCancel={() => endDrag()}
        onLostPointerCapture={() => endDrag()}
        aria-label={snap === 2 ? 'Collapse settings' : 'Expand settings'}
        aria-expanded={snap > 0}
        className="flex w-full shrink-0 touch-none flex-col items-center gap-1 px-4 pt-2.5 pb-1"
      >
        <span
          aria-hidden
          // Over the camera when collapsed: white with a shadow so it reads on any picture.
          className={`h-1.5 w-12 rounded-full ${snap === 0 ? 'bg-white/90 shadow-[0_1px_4px_rgba(15,23,42,0.6)]' : 'bg-line'}`}
        />
        {header && snap > 0 ? <span className="w-full">{header}</span> : null}
      </button>
      {snap > 0 ? (
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
          {children}
        </div>
      ) : null}
    </div>
  );
}
